import { createRouter, createWebHistory } from 'vue-router'
import TopView from '../views/TopView.vue'
import ReserveView from '../views/ReserveView.vue'
import ContactView from '../views/ContactView.vue'
import TradelawView from '../views/TradelawView.vue'
import TestView from '../views/TestView.vue'
import { trackPageView } from '../utils/analytics'
import { useAuthStore } from '@/stores/auth'

const routes = [
  {
    path: '/',
    name: 'home',
    component: TopView,
    meta: { title: 'トップ | 南青山一丁目の癒し・美容業専用レンタルスペース' }
  },
  {
    path: '/about',
    name: 'about',
    component: () => import(/* webpackChunkName: "about" */ '../views/AboutView.vue'),
    meta: { title: 'about | 南青山一丁目の癒し・美容業専用レンタルスペース' }
    
  },
  {
    path: '/thanks',
    name: 'thanks',
    component: () => import(/* webpackChunkName: "about" */ '../views/ThanksView.vue'),
    meta: { title: '送信完了 | 南青山一丁目の癒し・美容業専用レンタルスペース' }
  },
  {
    path: '/reserve',
    name: 'reserve',
    component: ReserveView,
    meta: { title: 'ご予約 | 南青山一丁目の癒し・美容業専用レンタルスペース' }
  },
  {
    path: '/contact',
    name: 'contact',
    component: ContactView,
    meta: { title: 'お問い合わせ | 南青山一丁目の癒し・美容業専用レンタルスペース' }
  },
  {
    path: '/tradelaw',
    name: 'tradelaw',
    component: TradelawView,
    meta: { title: '特定商取引法に基づく表記 | 南青山一丁目の癒し・美容業専用レンタルスペース' }
  },
  {
    path: '/test',
    name: 'test',
    component: TestView,
    meta: { title: 'Test Page' }
  },
  // メンテナンスページ
  {
    path: '/maintenance',
    name: 'maintenance',
    component: () => import('../views/MaintenanceView.vue'),
    meta: { title: 'メンテナンス中 | Kaon R' }
  },
  // Auth Routes
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/auth/LoginView.vue'),
    meta: { title: 'ログイン | Kaon R' }
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('../views/auth/RegisterView.vue'),
    meta: { title: '新規会員登録 | Kaon R' }
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('../views/auth/ForgotPasswordView.vue'),
    meta: { title: 'パスワード再設定 | Kaon R' }
  },
  // Mypage Routes
  {
    path: '/mypage',
    name: 'mypage',
    component: () => import('../views/mypage/MypageView.vue'),
    meta: { title: 'マイページ | Kaon R', requiresAuth: true }
  },
  {
    path: '/mypage/reservations',
    name: 'mypage-reservations',
    component: () => import('../views/mypage/ReservationHistoryView.vue'),
    meta: { title: '予約履歴 | Kaon R', requiresAuth: true }
  },
  {
    path: '/mypage/payment-history',
    name: 'mypage-payment-history',
    component: () => import('../views/mypage/PaymentHistoryView.vue'),
    meta: { title: '支払い履歴 | Kaon R', requiresAuth: true }
  },
  {
    path: '/mypage/new-reservation',
    name: 'new-reservation',
    component: () => import('../views/mypage/NewReservationView.vue'),
    meta: { title: '新規予約 | Kaon R', requiresAuth: true }
  },
  {
    path: '/mypage/payment-method',
    name: 'mypage-payment-method',
    component: () => import('../views/mypage/PaymentMethodView.vue'),
    meta: { title: 'カード情報変更 | Kaon R', requiresAuth: true }
  },
  // Admin Routes
  {
    path: '/admin',
    name: 'admin-dashboard',
    component: () => import('../views/admin/AdminDashboard.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/reservations',
    name: 'admin-reservations',
    component: () => import('../views/admin/AllReservationsView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/payments',
    name: 'admin-payments',
    component: () => import('../views/admin/AllPaymentsView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/sales',
    name: 'admin-sales',
    component: () => import('../views/admin/SalesView.vue'),
    meta: { title: '売上集計 | Kaon R', requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/users',
    name: 'admin-users',
    component: () => import('../views/admin/AllUsersView.vue'),
    meta: { title: 'ユーザー一覧 | Kaon R', requiresAuth: true, requiresAdmin: true }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// メンテナンスモード（true: /mypage配下をメンテナンス中にする）
const MAINTENANCE_MYPAGE = false

// ナビゲーションガード
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Wait for auth init
  if (authStore.loading) {
    await authStore.init()
  }

  // メンテナンスモード: /mypage配下へのアクセスをリダイレクト（管理者はスキップ）
  if (MAINTENANCE_MYPAGE && to.path.startsWith('/mypage') && !authStore.isAdmin) {
    next({ name: 'maintenance' })
    return
  }

  // Check if route requires authentication
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'login', query: { redirect: to.fullPath } })
    return
  }

  // Check if route requires admin privileges
  if (to.meta.requiresAdmin && !authStore.isAdmin) {
    next({ name: 'mypage' }) // Redirect non-admin users from admin routes
    return
  }

  // Redirect authenticated users from login/register pages
  if ((to.name === 'login' || to.name === 'register') && authStore.isAuthenticated) {
    next({ name: 'mypage' })
    return
  }

  next()
})

router.afterEach((to) => {
  // GA4ページビュー送信
  trackPageView(to.path, to.meta.title || document.title)
})

export default router
